"use client";

import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";

import { useAddWidgetDrawer } from "@/modules/widgets/hooks/useAddWidgetDrawer";
import { useEditWidget } from "@/modules/widgets/context/EditWidgetContext";
import { useEditWidgetDrawer } from "@/modules/widgets/hooks/useEditWidgetDrawer";
import { Widget, useDashboard } from "@/modules";
import { WidgetPreviewModal } from "./WidgetPreviewModal";

import { AddWidgetAIPanel } from "./AIPanel";
import { CustomWidgetPanel } from "./CustomWidgetPanel";
import { EditViaAIPanel } from "./EditViaAIPanel";
import { useEditWidgetViaAI } from "@/modules/widgets/hooks/useEditWidgetViaAI";
import { Button } from "@/components/atoms";
import { CheckIcon, SparklesIcon } from "@/components/icons";
import { useWidgetGlobalOperations } from "@/modules/global/hooks/useWidgetGlobalOperations";
import { TemplatePicker } from "@/components/organisms/TemplatePicker";
import { WidgetTemplate, WidgetTemplateConfig } from "@/modules/widget-templates";
import { LayoutTemplate } from "lucide-react";
import { sub } from "date-fns";

export interface AddWidgetDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit?: (prompt: string) => void;
    onWidgetCreated?: (widget: Widget) => void;
    isLoading?: boolean;
    feedbackMessage?: string;
    feedbackType?: "success" | "error" | "info";
    editWidget?: Widget;
    onWidgetUpdated?: (widget: Widget) => void;
    isDuplicating?: boolean;
}

export function Drawer({
    isOpen,
    onClose,
    onSubmit,
    onWidgetCreated,
    isLoading = false,
    feedbackMessage,
    feedbackType,
    editWidget,
    onWidgetUpdated,
    isDuplicating = false,
}: AddWidgetDrawerProps) {
    const isEditMode = !!editWidget && !isDuplicating;
    const isDuplicateMode = !!editWidget && isDuplicating;

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [templateConfig, setTemplateConfig] = useState<WidgetTemplateConfig | null>(null);
    const [templateKey, setTemplateKey] = useState(0);
    const [editTab, setEditTab] = useState<"manual" | "ai">("manual");
    const [previewWidget, setPreviewWidget] = useState<Widget | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [showExamplePreviewModal, setShowExamplePreviewModal] = useState(false);
    const [showEditPreviewModal, setShowEditPreviewModal] = useState(false);
    const { deleteWidget } = useWidgetGlobalOperations();
    const { openDuplicateDrawer, isSharedClone } = useEditWidget();
    // Preview widgets for the manual flow are now rendered locally with a synthetic id (-1)
    // and never written to the DB, so there is nothing to clean up on unmount.

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    const handleClose = useCallback(() => {
        setShowDeleteConfirm(false);
        setShowTemplatePicker(false);
        setTemplateConfig(null);
        setEditTab("manual");
        setShowExamplePreviewModal(false);
        setShowEditPreviewModal(false);
        // Manual preview widget is now a local synthetic object (id = -1) — nothing to delete on the server.
        setPreviewWidget(null);
        onClose();
    }, [onClose]);

    const {
        state: { selectedDateRange },
    } = useDashboard();

    const {
        prompt,
        handlePromptChange,
        refinementPrompt,
        handleRefinementPromptChange,
        handleConfirm,
        handleCancel,
        handleKeyDown,
        isButtonDisabled,
        isCreatingWidget,
        isProcessing,
        isReady,
        progressUpdates,
        panel,
        goToCustomPanel,
        goToAIPanel,
        handleCreateManualWidget,
        publishToAll,
        setPublishToAll,
        agentFeedbackMessage,
        agentClarificationItems,
        handleClarificationConfirm,
        handleFilterRejection,
        previewWidget: agentPreviewWidget,
        handlePreviewAccept,
        handlePreviewDiscard,
        setPreviewMode,
    } = useAddWidgetDrawer({
        isOpen,
        onClose: handleClose,
        onSubmit,
        onWidgetCreated,
        onPreviewReady: useCallback((widget: Widget) => {
            setPreviewWidget(widget);
            setIsPreviewLoading(false);
        }, []),
        isLoading,
        dashboardDate: selectedDateRange || { from: sub(new Date(), { months: 1 }), to: new Date() },
    });

    const handleTemplateSelected = useCallback(
        (template: WidgetTemplate) => {
            setTemplateConfig({ ...template.widgetConfig, title: template.name, description: template.description });
            setTemplateKey((k) => k + 1);
            setShowTemplatePicker(false);
            goToCustomPanel();
        },
        [goToCustomPanel]
    );

    const handleSharedWidgetSelected = useCallback(
        (widget: Widget) => {
            setShowTemplatePicker(false);
            handleClose();
            openDuplicateDrawer(widget, true);
        },
        [handleClose, openDuplicateDrawer]
    );



    const handleDeleteWidget = () => {
        if (!editWidget) return;

        try {
            deleteWidget(editWidget);
            handleClose();
        } catch (error) {
            console.error("Error deleting widget:", error);
        }
    };

    const { isUpdatingWidget, handleUpdateManualWidget } = useEditWidgetDrawer({
        onWidgetUpdated,
        dashboardDate: selectedDateRange || { from: sub(new Date(), { months: 1 }), to: new Date() },
    });

    const editViaAI = useEditWidgetViaAI({
        widget: editWidget ?? null,
        onWidgetUpdated,
        onClose: handleClose,
    });

    // Reset AI edit state when the drawer closes
    useEffect(() => {
        if (!isOpen) {
            editViaAI.reset();
            setShowEditPreviewModal(false);
        }
    }, [isOpen]);

    // Reset AI edit state when switching to a different widget (prevents contamination between edits)
    useEffect(() => {
        editViaAI.reset();
        setShowEditPreviewModal(false);
    }, [editWidget?.id]);

    const customFormRef = useRef<HTMLFormElement>(null);

    const [customState, setCustomState] = useState<{
        draft: any;
        isValid: boolean;
        isLoading?: boolean;
        hierarchyData?: any;
    }>({
        draft: {},
        isValid: false,
        isLoading: false,
        hierarchyData: undefined,
    });

    const handleCustomDraftChange = useCallback((state: any) => {
        setCustomState(state);
    }, []);

    const creationMode = panel === "ai" ? "ai" : "manual";

    const primaryButtonLabel = useMemo(() => {
        if (isDuplicateMode) {
            if (isSharedClone) return isCreatingWidget ? "Cloning…" : "Clone Widget";
            return isCreatingWidget ? "Duplicating…" : "Duplicate Widget";
        }
        if (isEditMode) {
            if (editTab === "ai") {
                if (editViaAI.previewWidget) return editViaAI.isAccepting ? "Saving…" : "Accept Changes";
                return editViaAI.isProcessing ? "Applying…" : "Apply Changes";
            }
            return isUpdatingWidget ? "Updating…" : "Update Widget";
        }
        if (creationMode === "ai") {
            if (isCreatingWidget || isProcessing) return "Generating…";
            if (agentPreviewWidget) return "Add to Dashboard";
            return "Generate Widget";
        }
        return isCreatingWidget ? "Creating…" : "Create Widget";
    }, [isEditMode, isDuplicateMode, isUpdatingWidget, creationMode, isCreatingWidget, isProcessing, editTab, editViaAI.isProcessing, editViaAI.previewWidget, editViaAI.isAccepting, agentPreviewWidget]);

    const isPrimaryDisabled = isEditMode
        ? editTab === "ai"
            ? editViaAI.previewWidget
                ? editViaAI.isAccepting
                : editViaAI.isDisabled
            : !customState.isValid || isUpdatingWidget || customState.isLoading
        : isDuplicateMode
          ? !customState.isValid || isCreatingWidget || customState.isLoading
          : creationMode === "ai"
            ? agentPreviewWidget ? isCreatingWidget || isProcessing : isButtonDisabled || isPreviewLoading
            : !customState.isValid || isCreatingWidget || customState.isLoading || isPreviewLoading;

    // Clear preview loading if creation ended without producing a preview widget (error path)
    useEffect(() => {
        if (isPreviewLoading && !isCreatingWidget && !isProcessing && !previewWidget) {
            setIsPreviewLoading(false);
        }
    }, [isPreviewLoading, isCreatingWidget, isProcessing, previewWidget]);

    const isPreviewDisabled = isEditMode || isDuplicateMode
        ? true
        : creationMode === "ai"
            ? !prompt.trim() || isCreatingWidget || isProcessing || !isReady || isPreviewLoading
            : !customState.isValid || isCreatingWidget || customState.isLoading || isPreviewLoading;

    const handlePreviewAction = useCallback(() => {
        setPreviewMode(true);
        setIsPreviewLoading(true);

        if (creationMode === "ai") {
            handleConfirm();
            return;
        }

        // Manual mode
        if (customFormRef.current && customFormRef.current.checkValidity()) {
            handleCreateManualWidget(customState.draft, customState.hierarchyData);
        } else if (customFormRef.current) {
            customFormRef.current.reportValidity();
        }
    }, [creationMode, handleConfirm, handleCreateManualWidget, customState.draft, customState.hierarchyData, setPreviewMode]);

    const handlePreviewConfirm = useCallback(() => {
        // Preview widget is synthetic (id = -1). Persist for real now via the same manual-create
        // path; the parent's onWidgetCreated callback (wired through useAddWidgetDrawer) will fire
        // once the API responds and close the drawer.
        setPreviewWidget(null);
        setIsPreviewLoading(false);
        if (customFormRef.current && customFormRef.current.checkValidity()) {
            handleCreateManualWidget(customState.draft, customState.hierarchyData);
        }
    }, [customState.draft, customState.hierarchyData, handleCreateManualWidget]);

    const handlePreviewClose = useCallback(() => {
        // Synthetic preview widget — nothing to delete on the server.
        setPreviewWidget(null);
        setIsPreviewLoading(false);
    }, []);

    const handlePrimaryAction = useCallback(() => {
        if (isEditMode) {
            if (editTab === "ai") {
                if (editViaAI.previewWidget) {
                    void editViaAI.handlePreviewAccept();
                } else {
                    void editViaAI.handleSubmit();
                }
                return;
            }
            if (customFormRef.current && customFormRef.current.checkValidity()) {
                handleUpdateManualWidget(customState.draft, customState.hierarchyData);
            } else if (customFormRef.current) {
                customFormRef.current.reportValidity();
            }
            return;
        }

        if (isDuplicateMode) {
            if (customFormRef.current && customFormRef.current.checkValidity()) {
                handleCreateManualWidget(customState.draft, customState.hierarchyData);
            } else if (customFormRef.current) {
                customFormRef.current.reportValidity();
            }
            return;
        }

        if (creationMode === "ai") {
            if (agentPreviewWidget) {
                handlePreviewAccept();
            } else {
                handleConfirm();
            }
            return;
        }

        if (customFormRef.current && customFormRef.current.checkValidity()) {
            handleCreateManualWidget(customState.draft, customState.hierarchyData);
        } else if (customFormRef.current) {
            customFormRef.current.reportValidity();
        }
    }, [isEditMode, isDuplicateMode, handleUpdateManualWidget, creationMode, handleConfirm, handlePreviewAccept, agentPreviewWidget, customState.draft, customState.hierarchyData, handleCreateManualWidget, editTab, editViaAI.handleSubmit]);

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) handleClose();
            }}
        >
            <AnimatePresence>
                {isOpen && (
                    <Dialog.Portal forceMount>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-9998 bg-[#0f1b21]/30"
                        />
                        <Dialog.Content asChild>
                            <motion.div
                                initial={{ x: 480 }}
                                animate={{ x: 0 }}
                                exit={{ x: 480 }}
                                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                                className="fixed right-0 top-0 z-9999 flex h-full w-full max-w-[480px] flex-col bg-white shadow-[-3px_0px_46px_0px_rgba(0,0,0,0.16)] focus:outline-none"
                            >
                                <div className="flex items-center justify-between border-b border-[#e0e0e0] px-6 py-2.5">
                                    <Dialog.Title className="text-xl/relaxed font-semibold text-[#2d3a3f] font-roboto">
                                        {isDuplicateMode ? (isSharedClone ? "Clone Widget" : "Duplicate Widget") : isEditMode ? "Edit Widget" : "Create Widget"}
                                    </Dialog.Title>
                                    <Dialog.Close asChild>
                                        <button className="text-[#5a717a] transition hover:text-[#2d3a3f]" aria-label="Close">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </Dialog.Close>
                                </div>

                                {!isEditMode && !isDuplicateMode && (
                                    <div className="px-6 py-2.5 space-y-3">
                                        <div className="flex w-full gap-2.5">
                                            <Button
                                                className={`w-full py-1.5 text-base/loose ${creationMode === "ai" && "font-bold"} font-roboto`}
                                                onClick={goToAIPanel}
                                                variant={creationMode === "ai" ? "primary" : "secondary"}
                                            >
                                                <SparklesIcon className={`mr-2 h-4 w-4 ${creationMode === "ai" ? "text-white" : "text-[#5a717a]"}`} /> Create AI
                                                Widget
                                            </Button>
                                            <Button
                                                className={`w-full py-1.5 text-base/loose ${creationMode === "manual" && "font-bold"} font-roboto`}
                                                onClick={goToCustomPanel}
                                                variant={creationMode === "manual" ? "primary" : "secondary"}
                                            >
                                                Create Manually
                                                {creationMode === "manual" ? (
                                                    <CheckIcon
                                                        className={`ml-2 h-4 w-4 ${creationMode === "manual" ? "text-black" : "text-white"}`}
                                                        bgColor={creationMode === "manual" ? "#fff" : "#000"}
                                                    />
                                                ) : null}
                                            </Button>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 border-t border-[#e0e0e0]" />
                                            <span className="text-xs text-[#5a717a] font-roboto">or</span>
                                            <div className="flex-1 border-t border-[#e0e0e0]" />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowTemplatePicker(true)}
                                            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[#079a95] px-3 py-2 text-sm font-medium text-[#079a95] transition hover:bg-[#e8f5f4] font-roboto"
                                        >
                                            <LayoutTemplate className="h-4 w-4" />
                                            Start from Template
                                        </button>
                                    </div>
                                )}

                                {isEditMode && (
                                    <div className="px-6 py-2.5">
                                        <div className="flex w-full gap-2.5">
                                            <Button
                                                className={`w-full py-1.5 text-base/loose ${editTab === "manual" ? "font-bold" : ""} font-roboto`}
                                                onClick={() => setEditTab("manual")}
                                                variant={editTab === "manual" ? "primary" : "secondary"}
                                            >
                                                Edit Manually
                                            </Button>
                                            <Button
                                                className={`w-full py-1.5 text-base/loose ${editTab === "ai" ? "font-bold" : ""} font-roboto`}
                                                onClick={() => setEditTab("ai")}
                                                variant={editTab === "ai" ? "primary" : "secondary"}
                                            >
                                                <SparklesIcon className={`mr-2 h-4 w-4 ${editTab === "ai" ? "text-white" : "text-[#5a717a]"}`} />
                                                Edit via AI
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <div className="relative flex-1 overflow-y-auto px-6 py-6">
                                    {isEditMode ? (
                                        editTab === "ai" ? (
                                            <EditViaAIPanel
                                                prompt={editViaAI.prompt}
                                                setPrompt={editViaAI.setPrompt}
                                                onSubmit={editViaAI.handleSubmit}
                                                isProcessing={editViaAI.isProcessing}
                                                isDisabled={editViaAI.isDisabled}
                                                progressSteps={editViaAI.progressSteps}
                                                feedbackMessage={editViaAI.feedbackMessage}
                                                onKeyDown={editViaAI.handleKeyDown}
                                                previewWidget={editViaAI.previewWidget}
                                                onPreviewDiscard={editViaAI.handlePreviewDiscard}
                                                onPreviewOpen={() => setShowEditPreviewModal(true)}
                                                onRefineSubmit={editViaAI.handleRefineSubmit}
                                                clarificationItems={editViaAI.clarificationItems}
                                                onClarificationConfirm={editViaAI.handleClarificationConfirm}
                                            />
                                        ) : (
                                            <CustomWidgetPanel onDraftChange={handleCustomDraftChange} formRef={customFormRef} editWidget={editWidget} isDuplicating={isDuplicateMode} isSharedClone={isSharedClone} />
                                        )
                                    ) : isDuplicateMode ? (
                                        <CustomWidgetPanel onDraftChange={handleCustomDraftChange} formRef={customFormRef} editWidget={editWidget} isDuplicating={isDuplicateMode} isSharedClone={isSharedClone} />
                                    ) : creationMode === "ai" ? (
                                        <AddWidgetAIPanel
                                            prompt={prompt}
                                            onPromptChange={handlePromptChange}
                                            onPromptKeyDown={handleKeyDown}
                                            onSubmit={handleConfirm}
                                            isSubmitDisabled={isButtonDisabled}
                                            publishToAll={publishToAll}
                                            onPublishToAllChange={setPublishToAll}
                                            feedbackMessage={agentFeedbackMessage ?? feedbackMessage}
                                            feedbackType={feedbackType}
                                            clarificationItems={agentClarificationItems}
                                            onClarificationConfirm={handleClarificationConfirm}
                                            onFilterReject={handleFilterRejection}
                                            examplePreview={agentPreviewWidget}
                                            onExamplePreviewOpen={() => setShowExamplePreviewModal(true)}
                                            onExamplePreviewAccept={handlePreviewAccept}
                                            onExamplePreviewDiscard={handlePreviewDiscard}
                                            isCreating={isCreatingWidget}
                                            isProcessing={isProcessing}
                                            progressUpdates={progressUpdates}
                                            refinementPrompt={refinementPrompt}
                                            onRefinementPromptChange={handleRefinementPromptChange}
                                            onRefinementKeyDown={handleKeyDown}
                                        />
                                    ) : (
                                        <CustomWidgetPanel key={templateKey} onDraftChange={handleCustomDraftChange} formRef={customFormRef} templateConfig={templateConfig} />
                                    )}
                                </div>

                                <TemplatePicker
                                    isOpen={showTemplatePicker}
                                    onClose={() => setShowTemplatePicker(false)}
                                    onSelectTemplate={handleTemplateSelected}
                                    onSelectSharedWidget={handleSharedWidgetSelected}
                                />

                                <div className="border-t border-[#e0e0e0] px-6 py-6">
                                    <div className="flex gap-3 relative">
                                        {isEditMode && !isDuplicateMode ? (
                                            <>
                                                {showDeleteConfirm && (
                                                    <div className="absolute -top-12 left-0 bg-white shadow-lg rounded-md p-3 z-50 border border-gray-200 w-48">
                                                        <p className="text-sm text-gray-700 mb-3 font-roboto">Are you sure you want to delete this widget?</p>
                                                        <div className="flex justify-between gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowDeleteConfirm(false)}
                                                                className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-700 transition-colors font-roboto"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={handleDeleteWidget}
                                                                className="text-xs px-3 py-1.5 bg-red-500 hover:bg-red-600 rounded text-white transition-colors font-roboto"
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                <Button type="button" variant="float" onClick={() => setShowDeleteConfirm(true)}>
                                                    Delete Widget
                                                </Button>
                                            </>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="flex-1 whitespace-nowrap rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm font-medium text-[#2d3a3f] transition hover:bg-[#f3f5f6] font-roboto"
                                        >
                                            Cancel
                                        </button>
                                        {!isEditMode && !isDuplicateMode && creationMode !== "ai" && (
                                            <button
                                                type="button"
                                                onClick={handlePreviewAction}
                                                disabled={isPreviewDisabled || isPreviewLoading}
                                                className={`flex-1 whitespace-nowrap rounded-lg border px-3 py-2 text-sm font-medium transition font-roboto ${
                                                    isPreviewDisabled || isPreviewLoading
                                                        ? "cursor-not-allowed border-[#e0e0e0] text-[#97a7ad]"
                                                        : "border-[#079a95] text-[#079a95] hover:bg-[#e8f5f4]"
                                                }`}
                                            >
                                                {isPreviewLoading ? "Loading…" : "Preview Widget"}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handlePrimaryAction}
                                            disabled={isPrimaryDisabled}
                                            className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-white transition font-roboto ${
                                                isPrimaryDisabled ? "cursor-not-allowed bg-[#97a7ad]" : "bg-[#079a95] hover:bg-[#067a76]"
                                            }`}
                                        >
                                            {primaryButtonLabel}
                                        </button>
                                    </div>
                                </div>
                                {previewWidget && (
                                    <WidgetPreviewModal
                                        widget={previewWidget}
                                        onConfirm={handlePreviewConfirm}
                                        onClose={handlePreviewClose}
                                    />
                                )}
                                {agentPreviewWidget && showExamplePreviewModal && (
                                    <WidgetPreviewModal
                                        widget={{ ...agentPreviewWidget, id: -1 } as Widget}
                                        onConfirm={() => { setShowExamplePreviewModal(false); handlePreviewAccept(); }}
                                        onClose={() => setShowExamplePreviewModal(false)}
                                        confirmLabel="Add to Dashboard"
                                        forceEnableConfirm
                                    />
                                )}
                                {editViaAI.previewWidget && showEditPreviewModal && (
                                    <WidgetPreviewModal
                                        widget={editViaAI.previewWidget}
                                        onConfirm={() => { setShowEditPreviewModal(false); void editViaAI.handlePreviewAccept(); }}
                                        onClose={() => setShowEditPreviewModal(false)}
                                        confirmLabel="Accept Changes"
                                        forceEnableConfirm
                                    />
                                )}
                            </motion.div>
                        </Dialog.Content>
                    </Dialog.Portal>
                )}
            </AnimatePresence>
        </Dialog.Root>
    );
}
